import { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Award, RefreshCw, ChevronRight, Target } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function StylistEarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/portal").then(res => {
      setData(res.data?.earnings || {});
    }).catch(() => toast.error("Failed to load earnings")).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;
  }

  const e = data || {};
  const dailySales = e.daily_sales || [];
  const tiers = (e.tiers || []).sort((a, b) => a.min_sales - b.min_sales);

  return (
    <div data-testid="stylist-earnings-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-6">
      <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">My Earnings</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div data-testid="earn-month-rev" className="bg-white border border-slate-200 rounded-2xl p-5">
          <DollarSign className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-emerald-600">${e.month_service_revenue || 0}</p>
          <p className="text-xs text-slate-400 mt-0.5">Month Service Revenue</p>
        </div>
        <div data-testid="earn-commission" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Award className="w-5 h-5 text-[#F59E0B] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">${e.commission || 0}</p>
          <p className="text-xs text-slate-400 mt-0.5">Commission Earned</p>
        </div>
        <div data-testid="earn-tips" className="bg-white border border-slate-200 rounded-2xl p-5">
          <TrendingUp className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">${e.tips || 0}</p>
          <p className="text-xs text-slate-400 mt-0.5">Tips</p>
        </div>
        <div data-testid="earn-total" className="bg-gradient-to-br from-[#1A1A1A] to-[#333] rounded-2xl p-5 text-white">
          <DollarSign className="w-5 h-5 text-white/70 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold">${e.total_pay || 0}</p>
          <p className="text-xs text-white/60 mt-0.5">Total Earnings</p>
        </div>
      </div>

      {/* Commission Tier */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Outfit'] font-semibold text-slate-900">Commission Tier Progress</h2>
          <span className="text-xs bg-[#FAFAFA] text-slate-600 px-2.5 py-1 rounded-full border border-slate-200">
            7-Day Sales: <strong>${e.week_sales || 0}</strong>
          </span>
        </div>

        {/* Tier ladder */}
        <div className="space-y-2 mb-4">
          {tiers.map((tier, i) => {
            const isActive = tier.label === e.commission_tier;
            const isReached = (e.week_sales || 0) >= tier.min_sales;
            return (
              <div key={tier.label} data-testid={`tier-${tier.label}`} className={`flex items-center gap-3 p-3 rounded-lg border ${isActive ? "bg-[#FAFAFA] border-rose-300" : isReached ? "border-[#C3E6CB] bg-[#D4EDDA]/30" : "border-slate-200"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? "bg-slate-900 text-white" : isReached ? "bg-emerald-600 text-white" : "bg-slate-50 text-slate-400"}`}>
                  {isReached ? "✓" : i + 1}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isActive ? "text-slate-900" : "text-slate-600"}`}>{tier.label}</p>
                  <p className="text-xs text-slate-400">${tier.min_sales}+ in 7 days</p>
                </div>
                <span className={`text-sm font-semibold ${isActive ? "text-slate-900" : "text-slate-400"}`}>{Math.round(tier.rate * 100)}%</span>
              </div>
            );
          })}
        </div>

        {e.next_tier && (
          <div data-testid="next-tier-nudge" className="bg-[#FEF3CD] border border-[#FCEABB] rounded-lg p-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-[#856404]" />
            <p className="text-sm text-[#856404]">
              <strong>${e.next_tier.gap}</strong> more in sales to reach <strong>{e.next_tier.label}</strong> ({Math.round(e.next_tier.rate * 100)}% commission)
            </p>
          </div>
        )}
      </div>

      {/* 7-Day Sales Chart */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 mb-4">7-Day Sales Breakdown</h2>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailySales} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E6E4E0" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#8A8A8A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#8A8A8A" }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={(v) => [`$${v}`, "Revenue"]} contentStyle={{ borderRadius: 8, border: "1px solid #E6E4E0", fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#1A1A1A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Extra Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <p className="text-xs text-slate-400 mb-1">Appointments Completed</p>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{e.appointments_completed || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
          <p className="text-xs text-slate-400 mb-1">Avg Ticket</p>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">${e.avg_ticket || 0}</p>
        </div>
      </div>
    </div>
  );
}
